import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

// Dynamic API URL - configured via NEXT_PUBLIC_API_URL in .env.local
const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

// Number of retry attempts for network errors
const MAX_RETRIES = 3;
// Delay between retries in milliseconds
const RETRY_DELAY = 1000;

/**
 * Check if the error is retryable (network-related errors)
 */
const isRetryableError = (error: AxiosError): boolean => {
  if (!error.code) return false;

  // Retry on connection reset, network errors, timeout
  const retryableCodes = [
    "ECONNRESET",
    "ENOTFOUND",
    "ETIMEDOUT",
    "ECONNREFUSED",
    "EAI_AGAIN",
    "Network Error",
    "timeout",
  ];

  return retryableCodes.includes(error.code) || error.code.startsWith("ECONN");
};

/**
 * Sleep utility for delays
 */
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Creates a new axios instance with retry logic
 */
const createApiInstance = () => {
  const instance = axios.create({
    baseURL: `${apiBaseUrl}/api`,
    timeout: 50000,
    withCredentials: false,
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      // You can add auth tokens here if needed
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Response interceptor with retry logic
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retryCount?: number;
      };

      // If no original request or already retried too many times
      if (!originalRequest) {
        return Promise.reject(error);
      }

      // Initialize retry count if not present
      originalRequest._retryCount = originalRequest._retryCount || 0;

      // Check if error is retryable and we haven't exceeded max retries
      if (
        isRetryableError(error) &&
        originalRequest._retryCount < MAX_RETRIES
      ) {
        originalRequest._retryCount += 1;

        console.log(
          `Retrying request (${originalRequest._retryCount}/${MAX_RETRIES}): ${originalRequest.url}`,
        );

        // Wait before retrying
        await sleep(RETRY_DELAY * originalRequest._retryCount);

        try {
          return await instance(originalRequest);
        } catch (retryError) {
          // If retry fails, throw the original error
          return Promise.reject(error);
        }
      }

      // Log the error for debugging
      console.error("API Error:", {
        code: error.code,
        message: error.message,
        url: originalRequest.url,
        method: originalRequest.method,
        status: error.response?.status,
      });

      return Promise.reject(error);
    },
  );

  return instance;
};

export const api = createApiInstance();

export const localUrl = apiBaseUrl;
